import { can, type MemberRole } from "@boem/core/shared";
import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError, type ConsentInfo, type Me } from "../api.ts";
import "../claude.css";

// The two pages the board's OAuth server sends people to when they connect
// Claude: sign in (Microsoft, or the dev picker locally), then approve. Both
// carry a signed copy of Claude's request in the address; it goes back to the
// server untouched as `oauth_query`.

/** The signed OAuth request in this page's address, exactly as the server signed it. */
function signedQuery(): string {
  const params = new URLSearchParams(window.location.search);
  const names = new Set(params.getAll("ba_param"));
  const out = new URLSearchParams();
  for (const [k, v] of params) if (k === "sig" || k === "ba_param" || names.has(k)) out.append(k, v);
  return out.toString();
}

const go = (url: string | undefined) => {
  if (url) window.location.href = url;
};

export const isOAuthPage = (path = window.location.pathname) => path === "/oauth/sign-in" || path === "/oauth/consent";

export function OAuthPage({ me }: { me: Me }) {
  return window.location.pathname === "/oauth/sign-in" ? <OAuthSignIn me={me} /> : <OAuthConsent />;
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="signin">
      <div className="signin-card oauth-card">
        <p className="eyebrow">Board &middot; connect an AI app</p>
        {children}
      </div>
    </div>
  );
}

function OAuthSignIn({ me }: { me: Me }) {
  const [devMembers, setDevMembers] = useState<{ id: string; name: string; role: string; isAdmin: boolean }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const q = signedQuery();

  useEffect(() => {
    if (me.signIn.dev) api.devMembers().then(setDevMembers, () => {});
  }, [me.signIn.dev]);

  const run = (p: Promise<{ url?: string }>) =>
    p.then(
      (r) => (r.url ? go(r.url) : setError("The sign-in didn't continue. Start again from your AI app.")),
      (e) => setError(e instanceof ApiError ? e.message : "Couldn't reach the board."),
    );

  if (!q) {
    return (
      <Shell>
        <h1>Nothing to sign in to</h1>
        <p>This page is part of connecting an AI app (Claude, ChatGPT, ...) to the board. Start from the app: add the Board as a connector, then choose Connect.</p>
      </Shell>
    );
  }
  return (
    <Shell>
      <h1>Sign in to connect your AI app</h1>
      <p>It will use the board as you. Sign in with your work Microsoft account first.</p>
      {me.signIn.microsoft ? (
        <button className="btn primary signin-ms" onClick={() => run(api.oauthSignInMicrosoft(q))}>
          Sign in with Microsoft
        </button>
      ) : (
        <div className="hint" style={{ marginTop: 0 }}>
          Microsoft sign-in isn&rsquo;t configured on this server yet.
        </div>
      )}
      <div className={`msg${error ? " err" : ""}`}>{error ?? ""}</div>
      {me.signIn.dev && (
        <div className="dev-login">
          <label>Development only &mdash; sign in as</label>
          <div className="asg-grid">
            {devMembers.map((m) => (
              <button key={m.id} className="btn" onClick={() => run(api.oauthDevSignIn(m.id, q))}>
                {m.name} <span className="n">{m.isAdmin ? "admin" : m.role}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

/** What someone with this role lets their AI app change. */
function changesFor(v: { role: string; isAdmin: boolean }): string {
  const who = { role: v.role as MemberRole, isAdmin: v.isAdmin };
  const list: string[] = [];
  if (can(who, "create")) list.push("log items");
  if (can(who, "note")) list.push("add notes and reactions");
  if (can(who, "claim")) list.push("claim items");
  if (can(who, "close")) list.push("close and reopen");
  if (can(who, "work")) list.push("log work sessions");
  if (can(who, "assign")) list.push("hand items to people");
  if (can(who, "nondev")) list.push("mark non-dev and resolved via Teams");
  return list.join(", ");
}

/** The scopes to grant: what the app asked for, less what the person unticked. Pure. */
export function grantedScopes(requested: string[], allow: { write: boolean; personal: boolean }): string[] {
  return requested.filter((s) => (s !== "board:write" || allow.write) && (s !== "board:personal" || allow.personal));
}

function OAuthConsent() {
  const params = new URLSearchParams(window.location.search);
  const notOnTeam = params.get("error") === "not_on_team";
  const requested = (params.get("scope") ?? "").split(" ").filter(Boolean);
  const q = signedQuery();
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allowWrite, setAllowWrite] = useState(true);
  // the personal board is its own box, unticked: the app reaches it only when the person ticks it (orgs.md 7)
  const [allowPersonal, setAllowPersonal] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (notOnTeam || !q) return;
    api.consentInfo(params.get("client_id") ?? "", params.get("redirect_uri") ?? "").then(setInfo, (e) =>
      setError(e instanceof ApiError && e.status === 401 ? "Your sign-in has expired. Start again from your AI app." : "Couldn't reach the board."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answer = async (accept: boolean) => {
    setBusy(true);
    setError(null);
    // a read-only role never grants board:write, whatever the box says
    const viewerOnly = !!info?.viewer && !can({ role: info.viewer.role as MemberRole, isAdmin: info.viewer.isAdmin }, "note");
    const dropWrite = accept && requested.includes("board:write") && (!allowWrite || viewerOnly);
    const dropPersonal = accept && requested.includes("board:personal") && !allowPersonal;
    const scope = dropWrite || dropPersonal ? grantedScopes(requested, { write: !dropWrite, personal: !dropPersonal }).join(" ") : undefined;
    try {
      const r = await api.oauthConsent(accept, q, scope);
      go(r.url ?? r.redirect_uri);
    } catch (e) {
      setBusy(false);
      setError(e instanceof ApiError ? e.message : "Couldn't reach the board.");
    }
  };

  if (notOnTeam || (info && !info.viewer)) {
    return (
      <Shell>
        <h1>Not on the board</h1>
        <p>
          {info ? (
            <>
              You&rsquo;re signed in as <b>{info.email}</b>, which isn&rsquo;t on the Board&rsquo;s team, so it can&rsquo;t connect an AI app.
            </>
          ) : (
            <>The account you signed in with isn&rsquo;t on the Board&rsquo;s team, so it can&rsquo;t connect an AI app.</>
          )}{" "}
          Ask an admin to add you, then connect again.
        </p>
        {q && (
          <div className="row" style={{ justifyContent: "flex-start" }}>
            <button className="btn" disabled={busy} onClick={() => answer(false)}>
              Back to your AI app
            </button>
          </div>
        )}
      </Shell>
    );
  }
  if (!q) {
    return (
      <Shell>
        <h1>Nothing to approve</h1>
        <p>This page is part of connecting an AI app to the board. Start from the app.</p>
      </Shell>
    );
  }
  if (!info) {
    return (
      <Shell>
        <p>{error ?? "Loading…"}</p>
      </Shell>
    );
  }

  const v = info.viewer!;
  // the app as the board will label what it writes ("AI · ChatGPT")
  const known = info.client?.app ?? { app: info.client?.name || "An AI app", verified: false };
  const app = known.app;
  const wantsWrite = requested.includes("board:write");
  const readOnlyRole = !can({ role: v.role as MemberRole, isAdmin: v.isAdmin }, "note");
  return (
    <Shell>
      <h1 className="oauth-title">
        {app} wants to use the board as <span className="oauth-who">{v.name}</span>
      </h1>
      <p className="oauth-sub">
        Signed in as {info.email}
        {info.redirectHost ? (
          <>
            {" "}
            &middot; returns you to <b>{info.redirectHost}</b>
          </>
        ) : null}
        {info.client?.verifiedHost ? <> &middot; identified by {info.client.verifiedHost}</> : null}
      </p>
      {!known.verified && (
        <div className="secret-hint" role="alert">
          <b>The board doesn&rsquo;t recognise this app.</b> It calls itself &ldquo;{app}&rdquo;, and what it writes will be marked &ldquo;AI &middot; {app}?&rdquo; (not
          verified). Only continue if you set this connection up yourself.
        </div>
      )}
      <ul className="oauth-perms">
        <li>
          <input type="checkbox" checked disabled aria-label="Read the board (required)" />
          <span>
            <b>Read the board</b>
            <span className="oauth-perm-d">items, notes, history and screenshots</span>
          </span>
        </li>
        {wantsWrite && (
          <li>
            <input
              id="oauth-write"
              type="checkbox"
              checked={allowWrite && !readOnlyRole}
              disabled={readOnlyRole}
              onChange={(e) => setAllowWrite(e.target.checked)}
            />
            <label htmlFor="oauth-write">
              <b>Make changes as {v.name}</b>
              <span className="oauth-perm-d">
                {readOnlyRole ? `your role is read-only, so ${app} can only read` : `${changesFor(v)} — only what your role (${v.isAdmin ? "admin" : v.role}) allows`}
              </span>
            </label>
          </li>
        )}
        {requested.includes("board:personal") && (
          <li>
            <input id="oauth-personal" type="checkbox" checked={allowPersonal} onChange={(e) => setAllowPersonal(e.target.checked)} />
            <label htmlFor="oauth-personal">
              <b>Your personal board</b>
              <span className="oauth-perm-d">
                read and change your own board, and boards people shared with you (as your role there allows). Unticked, {app} works on OEMUAP only
              </span>
            </label>
          </li>
        )}
      </ul>
      <p className="hint">
        Everything {app} writes shows under your name with an &ldquo;AI &middot; {app}&rdquo; label, and in each item&rsquo;s history as done by you
        through {app}. Credentials are refused as on the board.
        {requested.includes("offline_access") ? ` ${app} stays connected until you remove the connector (up to 30 days without use).` : ""}
      </p>
      <div className={`msg${error ? " err" : ""}`}>{error ?? ""}</div>
      <div className="row">
        <button className="btn" disabled={busy} onClick={() => answer(false)}>
          Deny
        </button>
        <button className="btn primary" disabled={busy} onClick={() => answer(true)}>
          Allow
        </button>
      </div>
    </Shell>
  );
}
